import { useServices } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import { emptyPaginatedResponse } from '@yawi/domain';
import { ReservationStatus } from '@yawi/types';
import type {
  AccessLog,
  ActivityFeedItem,
  AccessTodaySummary,
  Announcement,
  AmenityReservation,
  ChargeSummaryExtended,
  ChargesHistoryResponse,
  ChargingAgingResponse,
  DashboardAlert,
  DashboardAlertsResponse,
  DashboardInsight,
  DashboardKpisResponse,
  KpiCardDto,
  Unit,
  UpcomingExpense,
  UpcomingExpensesResponse,
} from '@yawi/types';
import { useEffect, useMemo, useState } from 'react';
import { deriveLocalInsight } from '@/modules/admin-v2/utils/deriveLocalInsight';

const EMPTY_INSIGHT: DashboardInsight = {
  type: 'none',
  headline: '',
  unitsCount: 0,
  metricLabel: '',
  potentialLoss: null,
  primaryCta: { label: '', action: '' },
  secondaryCta: null,
};

const EMPTY_CHARGE_SUMMARY: ChargeSummaryExtended = {
  totalPending: 0,
  totalPartial: 0,
  totalPaid: 0,
  amountPending: 0,
  amountPaid: 0,
  unitsCollected: 0,
  unitsPending: 0,
  targetAmount: 0,
  period: '',
};

const EMPTY_AGING: ChargingAgingResponse = { period: '', buckets: [], totalAmount: 0, totalUnits: 0 };
const EMPTY_HISTORY: ChargesHistoryResponse = { points: [] };
const EMPTY_ACCESS_TODAY: AccessTodaySummary = { date: '', accessesToday: 0, visitsToday: 0, incidentsToday: 0, accessesYesterday: 0, peakHourStart: null };
const EMPTY_ALERTS: DashboardAlertsResponse = { items: [], total: 0 };
const EMPTY_UPCOMING_EXPENSES: UpcomingExpensesResponse = { items: [] };
const EMPTY_KPIS: DashboardKpisResponse = { period: '', items: [] };

// Salta la llamada al backend (y el 403 que generaría) cuando el rol actual no tiene
// el permiso de lectura correspondiente, devolviendo un valor "vacío" ya resuelto.
function guarded<T>(allowed: boolean, emptyValue: T, fn: () => Promise<T>): Promise<T> {
  return allowed ? fn() : Promise.resolve(emptyValue);
}

export type UnitStats = {
  total: number;
  apartments: number;
  houses: number;
};

type DashboardData = {
  insight: DashboardInsight | null;
  alerts: DashboardAlert[];
  summary: ChargeSummaryExtended | null;
  aging: ChargingAgingResponse | null;
  history: ChargesHistoryResponse | null;
  upcoming: UpcomingExpense[];
  todaySummary: AccessTodaySummary | null;
  recentAccessLogs: AccessLog[];
  upcomingReservations: AmenityReservation[];
  pendingReservations: AmenityReservation[];
  latestAnnouncements: Announcement[];
  unitStats: UnitStats | null;
  kpis: KpiCardDto[];
  activity: ActivityFeedItem[];
};

type DashboardLoading = {
  insight: boolean;
  alerts: boolean;
  finanzas: boolean;
  todaySummary: boolean;
  recentAccessLogs: boolean;
  community: boolean;
  unitStats: boolean;
  pendingReservations: boolean;
  kpis: boolean;
  activity: boolean;
};

const INITIAL_DATA: DashboardData = {
  insight: null,
  alerts: [],
  summary: null,
  aging: null,
  history: null,
  upcoming: [],
  todaySummary: null,
  recentAccessLogs: [],
  upcomingReservations: [],
  pendingReservations: [],
  latestAnnouncements: [],
  unitStats: null,
  kpis: [],
  activity: [],
};

const INITIAL_LOADING: DashboardLoading = {
  insight: true,
  alerts: true,
  finanzas: true,
  todaySummary: true,
  recentAccessLogs: true,
  community: true,
  unitStats: true,
  pendingReservations: true,
  kpis: true,
  activity: true,
};

export function useDashboardData() {
  const {
    dashboardCondominiumRepo,
    accessLogRepo,
    unitRepo,
    amenityReservationRepo,
    announcementRepo,
  } = useServices();
  const ability = useAbility();

  const permissions = useMemo(() => ({
    canReadCharge: ability.can('read', 'Charge'),
    canReadExpense: ability.can('read', 'Expense'),
    canReadReservation: ability.can('read', 'Reservation'),
    canReadAccessLog: ability.can('read', 'AccessLog'),
    canReadUnit: ability.can('read', 'Unit'),
    canReadAnnouncement: ability.can('read', 'Announcement'),
    canReadTicket: ability.can('read', 'Ticket'),
    canReadAlerts: ability.can('read', 'Charge')
      || ability.can('read', 'Ticket')
      || ability.can('read', 'AccessLog')
      || ability.can('read', 'Poll'),
  }), [ability]);

  const [data, setData] = useState<DashboardData>(INITIAL_DATA);
  const [loading, setLoading] = useState<DashboardLoading>(INITIAL_LOADING);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(INITIAL_LOADING);
      setError(null);

      const results = await Promise.allSettled([
        guarded<DashboardInsight>(permissions.canReadCharge, EMPTY_INSIGHT, () => dashboardCondominiumRepo.getInsight()),
        guarded<DashboardAlertsResponse>(permissions.canReadAlerts, EMPTY_ALERTS, () => dashboardCondominiumRepo.getAlerts(6)),
        guarded<ChargeSummaryExtended>(permissions.canReadCharge, EMPTY_CHARGE_SUMMARY, () => dashboardCondominiumRepo.getChargesSummary()),
        guarded<ChargingAgingResponse>(permissions.canReadCharge, EMPTY_AGING, () => dashboardCondominiumRepo.getChargesAging()),
        guarded<ChargesHistoryResponse>(permissions.canReadCharge, EMPTY_HISTORY, () => dashboardCondominiumRepo.getChargesHistory(8)),
        guarded<UpcomingExpensesResponse>(permissions.canReadExpense, EMPTY_UPCOMING_EXPENSES, () => dashboardCondominiumRepo.getUpcomingExpenses(4)),
        guarded<AccessTodaySummary>(permissions.canReadAccessLog, EMPTY_ACCESS_TODAY, () => dashboardCondominiumRepo.getAccessTodaySummary()),
        guarded<AccessLog[]>(permissions.canReadAccessLog, [], () => accessLogRepo.getRecentLogs(5)),
        guarded(permissions.canReadUnit, emptyPaginatedResponse<Unit>(), async () => {
          // El backend limita a 100 por página: seguimos paginando para contar tipos sobre todas.
          const first = await unitRepo.getUnits({ page: 1, limit: 100 });
          const data = [...first.data];
          for (let page = 2; page <= (first.meta?.totalPages ?? 1); page++) {
            data.push(...(await unitRepo.getUnits({ page, limit: 100 })).data);
          }
          return { ...first, data };
        }),
        guarded<AmenityReservation[]>(permissions.canReadReservation, [], () => amenityReservationRepo.getUpcomingReservations()),
        guarded(permissions.canReadAnnouncement, emptyPaginatedResponse<Announcement>(), () => announcementRepo.getAnnouncements({ page: 1, limit: 3, sortBy: 'date', sortOrder: 'desc' })),
        guarded(permissions.canReadReservation, emptyPaginatedResponse<AmenityReservation>(), () => amenityReservationRepo.getReservations({ page: 1, limit: 100 })),
        guarded<DashboardKpisResponse>(permissions.canReadUnit || permissions.canReadAccessLog || permissions.canReadTicket, EMPTY_KPIS, () => dashboardCondominiumRepo.getKpis()),
        guarded(permissions.canReadAccessLog, { items: [] as ActivityFeedItem[] }, () => dashboardCondominiumRepo.getActivity(6, true)),
      ]);

      if (cancelled) return;

      const [
        insightRes,
        alertsRes,
        summaryRes,
        agingRes,
        historyRes,
        upcomingRes,
        todaySummaryRes,
        recentAccessRes,
        unitsRes,
        upcomingReservationsRes,
        announcementsRes,
        reservationsRes,
        kpisRes,
        activityRes,
      ] = results;

      const failed: string[] = [];

      const unitsPage = unitsRes.status === 'fulfilled' ? unitsRes.value : null;
      const unitStats: UnitStats | null = unitsPage
        ? (() => {
            const list = unitsPage.data;
            return {
              total: unitsPage.meta?.total ?? list.length,
              apartments: list.filter((u) => u.type === 'APARTMENT').length,
              houses: list.filter((u) => u.type === 'HOUSE').length,
            };
          })()
        : (failed.push('unidades'), null);

      const pendingReservations: AmenityReservation[] =
        reservationsRes.status === 'fulfilled'
          ? reservationsRes.value.data
              .filter((r) => r.status === ReservationStatus.PENDING)
              .sort((a, b) =>
                `${a.date}T${a.startTime}`.localeCompare(`${b.date}T${b.startTime}`)
              )
          : [];

      const summaryValue = summaryRes.status === 'fulfilled' ? summaryRes.value : (failed.push('cobranza'), null);
      const agingValue = agingRes.status === 'fulfilled' ? agingRes.value : (failed.push('mora'), null);
      const rawInsight = insightRes.status === 'fulfilled' ? insightRes.value : (failed.push('insight'), null);
      // El insight de ocupación no es relevante: derivamos uno local de mora/cobranza.
      const insight = rawInsight?.type === 'occupancy_drop'
        ? deriveLocalInsight(agingValue, summaryValue)
        : rawInsight;

      setData({
        insight,
        alerts: alertsRes.status === 'fulfilled' ? alertsRes.value.items : (failed.push('alertas'), []),
        summary: summaryValue,
        aging: agingValue,
        history: historyRes.status === 'fulfilled' ? historyRes.value : null,
        upcoming: upcomingRes.status === 'fulfilled' ? upcomingRes.value.items : [],
        todaySummary: todaySummaryRes.status === 'fulfilled' ? todaySummaryRes.value : (failed.push('accesos hoy'), null),
        recentAccessLogs: recentAccessRes.status === 'fulfilled' ? recentAccessRes.value : [],
        upcomingReservations: upcomingReservationsRes.status === 'fulfilled' ? upcomingReservationsRes.value : [],
        pendingReservations,
        latestAnnouncements: announcementsRes.status === 'fulfilled' ? announcementsRes.value.data : [],
        unitStats,
        kpis: kpisRes.status === 'fulfilled' ? kpisRes.value.items : [],
        activity: activityRes.status === 'fulfilled' ? activityRes.value.items : [],
      });

      if (failed.length > 0) {
        setError(`Algunos datos no pudieron cargarse: ${failed.join(', ')}`);
      }

      setLoading({
        insight: false,
        alerts: false,
        finanzas: false,
        todaySummary: false,
        recentAccessLogs: false,
        community: false,
        unitStats: false,
        pendingReservations: false,
        kpis: false,
        activity: false,
      });
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [dashboardCondominiumRepo, accessLogRepo, unitRepo, amenityReservationRepo, announcementRepo, permissions]);

  return { data, loading, error, permissions };
}
